/**
 * A CHAVE DE IDEMPOTÊNCIA É DO RASCUNHO, e não do clique (ADR-0019; mesmo desenho do Stonen).
 *
 * Ela nasce quando o rascunho é enviado pela primeira vez, fica guardada ANTES da chamada, e volta em
 * toda nova tentativa do MESMO conteúdo. É o que torna "tentar de novo" seguro: se a primeira resposta
 * se perdeu no caminho (o chamado nasceu na Cert4All e a tela não soube), o reenvio com a mesma chave
 * devolve o chamado que já existe em vez de abrir um segundo igual na fila do atendimento.
 *
 * ⚠️ Gerada AQUI, e não no servidor do Hugi: gerada lá, cada reenvio depois de resposta perdida viraria
 * chave nova e chamado duplicado — o caso exato que a chave existe para impedir.
 *
 * Conteúdo DIFERENTE é rascunho novo, e ganha chave nova (a mesma chave com outro texto é
 * `CHAVE_REUTILIZADA` lá). O vínculo é pela impressão do conteúdo (um hash): o navegador guarda só a
 * chave e a impressão, nunca o texto.
 *
 * `sessionStorage` é por aba e sobrevive a um recarregar — o caso da resposta perdida seguida de F5.
 * Quando ele não existe ou recusa (aba anônima, cota), a memória da página segura a mesma garantia
 * enquanto a aba estiver aberta.
 */

const PREFIXO = "hugi:chamados:rascunho:";

interface Guardado {
  readonly chave: string;
  readonly impressao: string;
}

const memoria = new Map<string, Guardado>();

function impressaoDe(conteudo: unknown): string {
  // FNV-1a de 32 bits: não é segurança, é só "este conteúdo é o mesmo de antes?".
  const texto = JSON.stringify(conteudo);
  let hash = 0x811c9dc5;
  for (let i = 0; i < texto.length; i += 1) {
    hash ^= texto.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16);
}

function novaChave(): string {
  const aleatorio = globalThis.crypto?.randomUUID?.();
  return aleatorio ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 14)}`;
}

function ler(escopo: string): Guardado | null {
  try {
    const bruto = globalThis.sessionStorage?.getItem(PREFIXO + escopo);
    if (bruto) {
      const valor = JSON.parse(bruto) as Partial<Guardado>;
      if (typeof valor.chave === "string" && typeof valor.impressao === "string") {
        return { chave: valor.chave, impressao: valor.impressao };
      }
    }
  } catch {
    /* armazenamento indisponível: cai para a memória */
  }
  return memoria.get(escopo) ?? null;
}

function gravar(escopo: string, valor: Guardado): void {
  memoria.set(escopo, valor);
  try {
    globalThis.sessionStorage?.setItem(PREFIXO + escopo, JSON.stringify(valor));
  } catch {
    /* idem */
  }
}

/** A chave deste rascunho: a mesma enquanto o conteúdo for o mesmo. */
export function chaveDoRascunho(escopo: string, conteudo: unknown): string {
  const impressao = impressaoDe(conteudo);
  const atual = ler(escopo);
  if (atual && atual.impressao === impressao) return atual.chave;
  const nova = { chave: novaChave(), impressao };
  gravar(escopo, nova);
  return nova.chave;
}

/** Depois do SUCESSO (e só dele): o próximo envio é outro rascunho. */
export function esquecerRascunho(escopo: string): void {
  memoria.delete(escopo);
  try {
    globalThis.sessionStorage?.removeItem(PREFIXO + escopo);
  } catch {
    /* idem */
  }
}
